import React, { useState, useEffect } from "react";
import { useAuth, API, formatApiErrorDetail } from "@/App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { Trash2, RotateCcw, Clock, User, Loader2 } from "lucide-react";

const RecentlyDeleted = () => {
  const { getAuthHeaders } = useAuth();
  const [deletedEvents, setDeletedEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restoringIds, setRestoringIds] = useState(new Set());

  useEffect(() => {
    fetchDeleted();
  }, []);

  const fetchDeleted = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/shifts/deleted`, { headers: getAuthHeaders() });
      setDeletedEvents(res.data);
    } catch (error) {
      console.error("Error fetching deleted shifts:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRestoreEvent = async (event) => {
    const shiftIds = event.shifts.map(s => s.id);
    const key = `${event.event_name}|${event.date}`;
    setRestoringIds(prev => new Set([...prev, key]));
    try {
      const res = await axios.post(`${API}/shifts/restore`, { shift_ids: shiftIds }, { headers: getAuthHeaders() });
      toast.success(res.data.message);
      fetchDeleted();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Failed to restore shifts");
    } finally {
      setRestoringIds(prev => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  const handleRestoreShift = async (shiftId, eventKey) => {
    setRestoringIds(prev => new Set([...prev, shiftId]));
    try {
      const res = await axios.post(`${API}/shifts/restore`, { shift_ids: [shiftId] }, { headers: getAuthHeaders() });
      toast.success(res.data.message);
      fetchDeleted();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Failed to restore shift");
    } finally {
      setRestoringIds(prev => {
        const next = new Set(prev);
        next.delete(shiftId);
        return next;
      });
    }
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="recently-deleted-page">
      <div>
        <h1 className="text-3xl font-bold font-heading text-[#1A1A1A]">Recently Deleted</h1>
        <p className="text-[#666666]">Recover deleted shifts from the last 30 days</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse text-[#666666]">Loading...</div>
        </div>
      ) : deletedEvents.length === 0 ? (
        <Card className="border border-[#E5E5E5] shadow-none">
          <CardContent className="py-12 text-center">
            <Trash2 className="w-12 h-12 mx-auto text-[#999999] mb-4" />
            <p className="text-[#666666]">No recently deleted shifts</p>
            <p className="text-sm text-[#999999] mt-1">Deleted shifts are recoverable for 30 days</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {deletedEvents.map((event, idx) => {
            const eventKey = `${event.event_name}|${event.date}`;
            const isRestoring = restoringIds.has(eventKey);
            const assignedShifts = event.shifts.filter(s => s.assigned_name);
            const openShifts = event.shifts.filter(s => !s.assigned_name);

            return (
              <Card key={idx} className="border border-[#E5E5E5] shadow-none" data-testid={`deleted-event-${idx}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg font-semibold text-[#1A1A1A]">
                        {event.event_name}
                      </CardTitle>
                      <div className="flex items-center gap-3 text-sm text-[#666666] mt-1">
                        <span>{event.date}</span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {event.start_time} - {event.end_time}
                        </span>
                        <span className="text-xs text-[#999999]">
                          Deleted: {new Date(event.deleted_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    <Button
                      onClick={() => handleRestoreEvent(event)}
                      disabled={isRestoring}
                      className="bg-[#6A1E2E] hover:bg-[#521422] text-white"
                      data-testid={`restore-event-${idx}`}
                    >
                      {isRestoring ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RotateCcw className="w-4 h-4 mr-1" />}
                      Restore All ({event.shifts.length})
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="pt-2">
                  <div className="space-y-1.5">
                    {event.shifts.map((shift) => {
                      const isShiftRestoring = restoringIds.has(shift.id);
                      return (
                        <div
                          key={shift.id}
                          className="flex items-center justify-between p-2 bg-[#FAFAFA] border border-[#E5E5E5] rounded-sm"
                        >
                          <div className="flex items-center gap-2 text-sm">
                            <User className="w-3.5 h-3.5 text-[#666666]" />
                            <span className={shift.assigned_name ? "font-medium text-[#1A1A1A]" : "text-[#999999] italic"}>
                              {shift.assigned_name || "Open Slot"}
                            </span>
                            {shift.positions_needed?.length > 0 && (
                              <span className="text-xs text-[#999999]">
                                ({shift.positions_needed.join(", ")})
                              </span>
                            )}
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRestoreShift(shift.id)}
                            disabled={isShiftRestoring}
                            className="text-[#6A1E2E] hover:bg-[#F0EAEB] h-7 text-xs"
                            data-testid={`restore-shift-${shift.id}`}
                          >
                            {isShiftRestoring ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3 mr-1" />}
                            Restore
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RecentlyDeleted;
